import {
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import {
  resolveMuiCoreTranslation,
  resolveMuiDataGridTranslation,
  resolveMuiDatePickerTranslation,
} from 'intl/muiTranslations';
import React, { PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';
import { Color } from 'shared/utils/colors';

export const ThemeProviderWrapper = ({
  children,
}: PropsWithChildren<{}>): React.ReactElement => {
  const { locale } = useIntl();
  const theme = createTheme(
    {
      //TODO https://hustro.atlassian.net/browse/PT-5934
      // direction: getTextDirectionByLocale(intl.locale),
      palette: {
        primary: {
          main: Color.primaryOrange,
          light: Color.primaryLightOrange,
          contrastText: Color.black,
        },
        secondary: {
          main: '#7D5260',
          light: '#FFD8E4',
          contrastText: '#fff',
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              // borderRadius: 8,
              borderWidth: '2px !important',
            },
            outlined: {
              color: 'black',
            },
            containedPrimary: {
              transition:
                'box-shadow 0.15s ease-in-out, background-color 0.15s ease-in-out',
              // Slightly deeper but still vivid yellow on hover (no muddy-olive
              // darkening), paired with a subtle elevation for affordance.
              '&:hover': {
                backgroundColor: Color.primaryOrangeHoverDeep,
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.20)',
              },
            },
            text: {
              color: 'black',
              '&:hover': {
                backgroundColor: Color.primaryOrangeHover,
              },
            },
          },
        },

        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: Color.primaryOrange,
              },
            },
            // notchedOutline: {
            //   // borderColor: '#e6e6e6',
            //   borderColor: 'transparent',
            // },
          },
        },
        MuiFormControl: {
          defaultProps: {
            variant: 'outlined',
            // margin: 'dense',
          },
        },
        MuiInputLabel: {
          defaultProps: {
            variant: 'outlined',
          },
          styleOverrides: {
            root: ({ theme }) => ({
              color: Color.textIcon,
              '&.Mui-focused': {
                color: Color.primaryOrangeDark,
              },
              '&.Mui-focused.Mui-error': {
                color: theme.palette.error.main,
              },
            }),
          },
        },
        MuiTextField: {
          defaultProps: {
            variant: 'outlined',
          },
        },
        // Dropdown option states (Autocomplete + Select) — the MUI defaults
        // (~4% hover / ~8% selected) are too faint. Match the DataGrid scheme:
        // neutral hover, brand-yellow selected, stronger when selected + hovered.
        MuiAutocomplete: {
          styleOverrides: {
            option: {
              '&:hover': {
                backgroundColor: Color.scrimBlack06,
              },
              '&.Mui-focused': {
                backgroundColor: Color.scrimBlack06,
              },
              '&[aria-selected="true"]': {
                backgroundColor: Color.primaryOrangeHover,
                '&:hover': {
                  backgroundColor: Color.primaryOrangeActive,
                },
                '&.Mui-focused': {
                  backgroundColor: Color.primaryOrangeActive,
                },
              },
            },
            // Keep the icon-button itself as the single hit-target so the pointer
            // cursor does not flicker as the mouse crosses the X/▼ glyph; align
            // hover with the app-wide brand-yellow icon-button treatment.
            clearIndicator: {
              '& svg': {
                pointerEvents: 'none',
              },
              '&:hover': {
                backgroundColor: Color.primaryOrangeHover,
              },
            },
            popupIndicator: {
              '& svg': {
                pointerEvents: 'none',
              },
              '&:hover': {
                backgroundColor: Color.primaryOrangeHover,
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              '&:hover': {
                backgroundColor: Color.scrimBlack06,
              },
              '&.Mui-selected': {
                backgroundColor: Color.primaryOrangeHover,
                '&:hover': {
                  backgroundColor: Color.primaryOrangeActive,
                },
                '&.Mui-focusVisible': {
                  backgroundColor: Color.primaryOrangeActive,
                },
              },
            },
          },
        },
      },
      typography: {
        fontFamily: 'Archivo, Arial',
        h1: {
          fontSize: '2rem',
          fontWeight: 'bold',
        },
        h3: {
          fontSize: '1rem',
          fontWeight: 'bold',
        },
        h4: {
          fontSize: '1rem',
          color: '#979797',
        },
        button: {
          lineHeight: '1.8',
        },
      },
    },
    resolveMuiDatePickerTranslation(locale),
    resolveMuiCoreTranslation(locale),
    resolveMuiDataGridTranslation(locale)
  );
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}> {children} </ThemeProvider>
    </StyledEngineProvider>
  );
};
